import type { AccountSession, Session } from './types'

/**
 * Capability names the UI knows about. The server is always authoritative —
 * these decide what to *offer*, never what to allow. A route guarded here still
 * gets a 403 from the API if the account lost the capability mid-session, and
 * that 403 has its own surface.
 */
export const CAPABILITIES = {
  financeRegistersRead: 'finance.registers.read',
  financeRegistersManage: 'finance.registers.manage',
  financeRegistersOperate: 'finance.registers.operate',
  financeRegistersReconcile: 'finance.registers.reconcile',
  legalEntitiesRead: 'legal_entities.read',
  legalEntitiesWrite: 'legal_entities.write',
  appAccess: 'app.access',
  releaseMaintenanceAccess: 'release.maintenance.access',
  clinicMaintenanceManage: 'clinic.maintenance.manage',
  trackerIssuesReport: 'tracker.issues.report',
  trackerIssuesSubmit: 'tracker.issues.submit',
  trackerIssuesBrowse: 'tracker.issues.browse',
  clinicUsersRead: 'clinic.users.read',
  clinicUsersWrite: 'clinic.users.write',
  clinicOwnershipTransfer: 'clinic.ownership.transfer',
  clinicModulesRead: 'clinic.modules.read',
  clinicModulesWrite: 'clinic.modules.write',
  clinicSettingsRead: 'clinic.settings.read',
  clinicSettingsWrite: 'clinic.settings.write',
  patientsRead: 'patients.read',
  patientsIdentityRead: 'patients.identity.read',
  patientLeadsRead: 'patient_leads.read',
  patientLeadsWrite: 'patient_leads.write',
  referralPartnersRead: 'referral_partners.read',
  referralPartnersManage: 'referral_partners.manage',
  referralRewardsRead: 'referral_rewards.read',
  referralRewardsManage: 'referral_rewards.manage',
  tasksRead: 'tasks.read',
  tasksWrite: 'tasks.write',
  tasksRemindersSend: 'tasks.reminders.send',
  // One capability for the whole personal inbox: listing it and acknowledging
  // one's own rows are the same reading act, so there is no notifications.write.
  notificationsRead: 'notifications.read',
  patientsWrite: 'patients.write',
  practitionersRead: 'practitioners.read',
  patientCasesRead: 'patient_cases.read',
  patientCasesWrite: 'patient_cases.write',
  patientCasesActivate: 'patient_cases.activate',
  patientCasesComplete: 'patient_cases.complete',
  patientCasesCancel: 'patient_cases.cancel',
  patientHospitalStaysRead: 'patient_hospital_stays.read',
  patientHospitalStaysWrite: 'patient_hospital_stays.write',
  patientHospitalStaysAdmit: 'patient_hospital_stays.admit',
  patientHospitalStaysDischarge: 'patient_hospital_stays.discharge',
  patientHospitalStaysCancel: 'patient_hospital_stays.cancel',
  patientMessagesRead: 'patient_messages.read',
  messagesRead: 'messages.read',
  messagesSend: 'messages.send',
  scheduleRead: 'schedule.read',
  availabilityRead: 'schedule.availability.read',
  availabilityManage: 'schedule.availability.manage',
  resourcesRead: 'schedule.resources.read',
  resourcesManage: 'schedule.resources.manage',
  workingCalendarsRead: 'schedule.working_calendars.read',
  workingCalendarsManage: 'schedule.working_calendars.manage',
  attachmentsRead: 'documents.read',
  attachmentsWrite: 'documents.write',
  laboratoryResultsRead: 'laboratory.results.read',
  laboratoryResultsWrite: 'laboratory.results.write',
  vitalsRead: 'vitals.read',
  vitalsWrite: 'vitals.write',
  imagingStudiesRead: 'imaging_studies.read',
  imagingStudiesWrite: 'imaging_studies.write',
  /*
   * The Person-document processing family. Four verbs because reading and
   * polling, uploading, starting/canceling, and purging derived bytes are each
   * gated separately server-side.
   */
  documentsRead: 'documents.read',
  documentsWrite: 'documents.write',
  documentsProcess: 'documents.process',
  documentsPurge: 'documents.purge',
  clinicalEncounterRead: 'clinical.encounters.read',
  clinicalEncounterStart: 'clinical.encounters.start',
  clinicalEncounterFinish: 'clinical.encounters.finish',
  clinicalRecordsRead: 'clinical.records.read',
  clinicalRecordsWrite: 'clinical.records.write',
  serviceRequestsRead: 'clinical.service_requests.read',
  serviceRequestsWrite: 'clinical.service_requests.write',
  serviceRequestsActivate: 'clinical.service_requests.activate',
  clinicalRecordsFinalize: 'clinical.records.finalize',
  financeRead: 'finance.read',
  financePriceListRead: 'finance.price_list.read',
  financePriceListManage: 'finance.price_list.manage',
  financeServicesWrite: 'finance.services.write',
  /*
   * The treatment-estimate family. The external-cost catalogue is a global
   * finance dictionary like the price list, read by everyone who builds an
   * estimate and managed by the few who curate it; estimates themselves are
   * patient documents, so their routes add `patients.read` on the server.
   */
  financeExternalCostCatalogRead: 'finance.external_cost_catalog.read',
  financeExternalCostCatalogManage: 'finance.external_cost_catalog.manage',
  financeEstimatesRead: 'finance.estimates.read',
  financeEstimatesWrite: 'finance.estimates.write',
  /*
   * The financial-operations family behind the financial_operator,
   * financial_supervisor and financial_period_manager role templates.
   * Corrections and refunds are supervisor authority distinct from routine
   * settlement, and period authority is granted separately; the server
   * remains authoritative for every refusal.
   */
  financeAccountsRead: 'finance.accounts.read',
  financeAccountsManage: 'finance.accounts.manage',
  financeAgreementsRead: 'finance.agreements.read',
  financeAgreementsManage: 'finance.agreements.manage',
  financeAllocationsWrite: 'finance.allocations.write',
  financeCorrectionsWrite: 'finance.corrections.write',
  financeCoverageRead: 'finance.coverage.read',
  financeCoverageWrite: 'finance.coverage.write',
  financePayerBillingRead: 'finance.payer_billing.read',
  financePayerBillingWrite: 'finance.payer_billing.write',
  financePeriodsRead: 'finance.periods.read',
  financePeriodsClose: 'finance.periods.close',
  financePeriodsReopen: 'finance.periods.reopen',
  financePrepaidRead: 'finance.prepaid.read',
  financePrepaidWrite: 'finance.prepaid.write',
  financeProfileRead: 'finance.profile.read',
  financeProfileManage: 'finance.profile.manage',
  financeReceiptsWrite: 'finance.receipts.write',
  financeRefundsWrite: 'finance.refunds.write',

  /*
   * The print-form capabilities, and they are not one family with one meaning.
   *
   * `print_forms.manage` and `print_forms.publish` are what Core gates the
   * admin print-form registry on, and they are the pair Core maps onto the two
   * privileges the separate editor service knows — see `pfePrivileges` below.
   * `print_forms.render` gates the clinical render command, which is a Record's
   * action rather than a Control Center one.
   *
   * `print_forms.read` is the Control Center's read-only print-form
   * capability: it lets an account whose only administrative business is print
   * forms reach the workspace and read registry metadata, without holding the
   * clinic's settings or the editor's authoring authority.
   */
  printFormsRead: 'print_forms.read',
  printFormsManage: 'print_forms.manage',
  printFormsPublish: 'print_forms.publish',
  printFormsRender: 'print_forms.render',
  aiPromptsRead: 'ai.prompts.read',
  aiPromptsRun: 'ai.prompts.run',
  aiPromptsManage: 'ai.prompts.manage',
  aiTestersRun: 'ai.testers.run',
  referenceDataRead: 'reference_data.read',
  protocolsRead: 'protocols.read',
  protocolsManage: 'protocols.manage',
  protocolsPublish: 'protocols.publish',
  protocolsActivate: 'protocols.activate',
  clinicalProtocolsSelect: 'clinical.protocols.select',
  visitTypesRead: 'schedule.visit_types.read',
  visitTypesManage: 'schedule.visit_types.manage',
  /*
   * Role-definition management. Both are developer-audience, platform-scoped
   * grants that Core reconciles only into the sealed `developer` role, and
   * Core refuses `/api/v1/admin/authorization/**` to any account that is not
   * an exact developer whatever strings it carries — so the Roles section's
   * offer predicate checks the account kind as well as the capability.
   */
  authorizationRolesRead: 'authorization.roles.read',
  authorizationRolesManage: 'authorization.roles.manage',
  /* The shard's communications connectors (#335): reading the redacted
     inventory and managing it are two grants, held independently. */
  connectorsRead: 'communications.connectors.read',
  connectorsManage: 'communications.connectors.manage',
  /* The correspondence foundation (#337): a Person's endpoints and policies,
     the clinic's routes, and the unified conversation record. The chart's
     Messages tab reads the record with `correspondenceRead` and falls back
     to the Patient email projection under `patientMessagesRead` without it;
     the composer offers a chat channel only under `correspondenceWrite` with
     the Person and route reads beside it (#344). */
  communicationsPersonsRead: 'communications.persons.read',
  communicationsPersonsManage: 'communications.persons.manage',
  communicationsRoutesRead: 'communications.routes.read',
  correspondenceRead: 'communications.correspondence.read',
  correspondenceWrite: 'communications.correspondence.write',
} as const

export type CapabilityName = (typeof CAPABILITIES)[keyof typeof CAPABILITIES]

/**
 * One decision, used by both the navigation and the route loaders. Keeping it
 * in a single function is what stops a link appearing for a capability the
 * route would then refuse, or the reverse.
 */
export function hasCapability(
  session: AccountSession | undefined,
  capability: CapabilityName,
): boolean {
  return session?.context.capabilities.includes(capability) ?? false
}

/**
 * ANY of a set, for a surface more than one capability can reach.
 *
 * Stated as its own function rather than as an `||` at each call site, because
 * the set itself is the thing that must not drift: the Control Center route and
 * the navigation tile that leads to it have to admit exactly the same accounts,
 * and two hand-written disjunctions are two places to get that wrong.
 */
export function hasAnyCapability(
  session: Session | undefined,
  capabilities: readonly CapabilityName[],
): boolean {
  return capabilities.some((capability) => hasCapability(session, capability))
}

export function isDeveloperSession(session: Session | undefined): boolean {
  return session?.identity.accountKind === 'developer'
}

/**
 * WHO ADMINISTERS THIS CLINIC, as a role rather than as a capability.
 *
 * Every other gate in the product asks what the session may DO, and that is the
 * right question for a feature: a capability is granted per clinic and Core
 * refuses the endpoint to match. This one asks who somebody IS, and it exists
 * for the narrow case a capability cannot express — a surface being kept out of
 * clinical hands while it is superseded, where the account that may still reach
 * it is the one that would be asked to explain the change.
 *
 * The owner and the developer are included because both already administer the
 * clinic by other routes, and a gate they could step around would be a gate
 * that lies about who it admits.
 *
 * DO NOT reach for this to gate a feature. A feature is gated by its capability
 * and refused by name; this decides only whether a superseded surface is still
 * offered at all.
 */
export function isClinicAdministrator(session: Session | undefined): boolean {
  if (session === undefined) return false
  return (
    session.identity.isClinicOwner ||
    session.identity.roles.includes('clinic_admin') ||
    isDeveloperSession(session)
  )
}

export const AI_PROMPT_CAPABILITIES = [
  CAPABILITIES.aiPromptsRead,
] as const

/**
 * Who may see the print-form registry section.
 *
 * Both, because the two say different things and neither implies the other
 * here: `print_forms.read` is read-only metadata access, while
 * `print_forms.manage` is the existing administrative capability that still
 * reads the same registry metadata and maps to editor authority below.
 */
export const PRINT_FORM_REGISTRY_CAPABILITIES = [
  CAPABILITIES.printFormsRead,
  CAPABILITIES.printFormsManage,
] as const

export const SCHEDULE_AREA_CAPABILITIES = [
  CAPABILITIES.scheduleRead,
  CAPABILITIES.workingCalendarsRead,
  CAPABILITIES.availabilityRead,
  CAPABILITIES.resourcesRead,
  CAPABILITIES.visitTypesRead,
] as const

export const LEADS_AREA_CAPABILITIES = [
  CAPABILITIES.patientLeadsRead,
  CAPABILITIES.referralPartnersRead,
] as const

/**
 * Who may enter the Finance staff workspace at all: one read per door.
 * Accounts (and the people who own them) under `finance.accounts.read`,
 * prepaid purchases under `finance.prepaid.read`, the price lists and taxes
 * under `finance.price_list.read`, the third-party cost catalogue under
 * `finance.external_cost_catalog.read`, the clinic profile under
 * `finance.profile.read` and the monthly periods under
 * `finance.periods.read` — a period manager holds neither accounts nor
 * clinical grants and still reaches the periods.
 */
export const FINANCE_AREA_CAPABILITIES = [
  CAPABILITIES.financeRegistersRead,
  CAPABILITIES.financeAccountsRead,
  CAPABILITIES.financePrepaidRead,
  CAPABILITIES.financePriceListRead,
  CAPABILITIES.financeExternalCostCatalogRead,
  CAPABILITIES.financeProfileRead,
  CAPABILITIES.financePeriodsRead,
] as const

/**
 * The privileges the separate print-form editor knows about.
 *
 * They are PFE's vocabulary, not Core's. They appear here because the editor
 * entry point is offered on the strength of what this session could obtain
 * there, and that derivation has to be stated somewhere the UI can read.
 */
export const PFE_PRIVILEGES = ['pfe_editor', 'pfe_publisher'] as const
export type PfePrivilege = (typeof PFE_PRIVILEGES)[number]

/**
 * What this session would be granted in the editor.
 *
 * The mapping mirrors `pfePrivilegesFor` in
 * `apps/api/internal/coreapi/pfe_session_http.go`. The browser derives the same
 * answer from the SESSION'S OWN capability list rather than from the route it
 * is on or anything it stored, so an account that would be refused a PFE
 * principal is never offered the editor in the first place. Core stays
 * authoritative: it introspects the session itself when PFE asks, and answers
 * `403` when no privilege follows.
 *
 * Publisher does NOT imply editor here even though PFE treats it that way,
 * for the reason Core states: each privilege follows from its own capability,
 * so a capability list that grew or shrank cannot be reconstructed wrongly from
 * one implication.
 */
export function pfePrivileges(session: Session | undefined): readonly PfePrivilege[] {
  const privileges: PfePrivilege[] = []
  if (!isDeveloperSession(session)) return privileges
  if (hasCapability(session, CAPABILITIES.printFormsManage)) {
    privileges.push('pfe_editor')
  }
  if (hasCapability(session, CAPABILITIES.printFormsPublish)) {
    privileges.push('pfe_publisher')
  }
  return privileges
}

/** The editor entry point's rule, in one place for the link and the route. */
export function canOpenPrintFormEditor(session: Session | undefined): boolean {
  return pfePrivileges(session).length > 0
}
